import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import type { IconName } from '@deepdive-icons';
import { Icon } from '../../atoms/Icon/Icon';
import { Badge, type BadgeType } from '../../atoms/Badge/Badge';
import { FilterChipCounter } from '../../molecules/FilterChipCounter/FilterChipCounter';
import { Stack } from '../../primitives/Stack/Stack';
import { SidebarHeader } from '../../molecules/SidebarHeader/SidebarHeader';
import { SidebarItemGroup } from '../../molecules/SidebarItemGroup/SidebarItemGroup';
import { SidebarItem } from '../../molecules/SidebarItem/SidebarItem';
import { AICredits } from '../../molecules/AICredits/AICredits';
import {
  SidebarFooter,
  type SidebarFooterProfile,
} from '../../molecules/SidebarFooter/SidebarFooter';
import { NotificationButton } from '../../molecules/NotificationButton/NotificationButton';
import styles from './Sidebar.module.css';

/**
 * `label` is the text the badge draws — "Beta", or the bare count on the
 * `Actions` rows. `srLabel` is what it is called out loud when that text does
 * not stand on its own, which is the whole of what the counter's `label` used
 * to carry on those rows.
 */
export type SidebarBadge = { type: BadgeType; label: string; srLabel?: string };

/**
 * The nav row's count badge. Figma's `_sidebar item` instances
 * `_filter chip counter` in this slot, so it lost its `type` axis when the
 * `Counter` atom was retired on 2026-09-02 — there is one count badge in the
 * system now and it has one appearance. (Those instances still carry a local
 * override to the deleted `component/counter/error-surface`, which is why they
 * render as red pills in Figma; flagged in plan.md §7.)
 */
export type SidebarCounter = { count: number; label?: string };

export type SidebarNavItem = {
  id: string;
  label: string;
  icon?: IconName;
  href?: string;
  badge?: SidebarBadge;
  counter?: SidebarCounter;
};

/** A labelled run of nav items. The first section in the design has no label. */
export type SidebarSection = {
  id: string;
  label?: string;
  items: SidebarNavItem[];
};

/**
 * An entry for a `SidebarMenuItem`. Figma's `_sidebar menu item` (48:2726) is
 * still a live component set, but nothing in the `Sidebar` composes it — the
 * footer menu it used to fill is built from `_sidebar item` rows.
 * Kept for callers building their own menus from the molecule.
 */
export type SidebarMenuEntry = {
  id: string;
  label: string;
  icon?: IconName;
  href?: string;
  badge?: SidebarBadge;
  counter?: SidebarCounter;
};

/**
 * A row in the footer menu — Figma's `_sidebar item` rows in `sidebar footer
 * menu` (2130:51323). A link when it has `href`, a button when it has
 * `onClick`, and inert with neither (FAQ, for now).
 */
export type SidebarFooterMenuItem = {
  id: string;
  label: string;
  icon?: IconName;
  href?: string;
  onClick?: () => void;
};

export type SidebarFooterConfig = {
  /** The workspace on the trigger. */
  name: string;
  avatarSrc: string;
  /**
   * Figma `AI credits` (2130:59087). It sat above the trigger until
   * 2026-09-22; it is the footer menu's lower `Bottom panel` now, so it only
   * shows while the menu is open. Omitted, not rendered.
   */
  credits?: { left: number; total: number };
  /** The menu's profile header — the signed-in person. */
  profile?: SidebarFooterProfile;
  /** The menu's rows, in order. */
  menu?: SidebarFooterMenuItem[];
  /**
   * Figma's `Sign out`. A `Button` in the `Bottom panel` until 2026-09-22 and
   * the last row of the menu since, which is where this puts it — after
   * `menu`, with the `logout` glyph. Omitted, there is no such row.
   */
  onSignOut?: () => void;
  signOutLabel?: string;
  /** Controlled open state for the footer menu. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * The header's right-hand slot — a `Buttons/Notification` instance (265:7545) on
 * `State=Active` carrying an `inbox` icon and the unread dot.
 *
 * The 2026-09 update removed that instance from `Sidebar Header` (48:2881),
 * which is back to holding the `Logomark` alone. The prop is kept rather than
 * deleted: `NotificationButton` is still a live component in the file, the slot
 * is optional, and nothing renders unless a caller passes it. The Figma-parity
 * story no longer does.
 */
export type SidebarNotification = {
  /** Accessible name for the button. */
  label: string;
  icon?: IconName;
  /**
   * Figma `State=Active`. False or absent renders `State=Default` — the button
   * with no dot. This was a `count` until 2026-09-02, when the design replaced
   * the counter pill with a plain indicator dot.
   */
  unread?: boolean;
  /** How the dot is announced, e.g. "unread messages". */
  unreadLabel?: string;
  onClick?: () => void;
};

export type SidebarProps = {
  sections: SidebarSection[];
  /** `id` of the selected nav item. */
  activeItemId?: string;
  onSelect?: (id: string) => void;
  footer?: SidebarFooterConfig;
  /** Replaces the default logomark in the header. */
  header?: ReactNode;
  /** The header's notification button. Omitted, the brand sits alone. */
  notification?: SidebarNotification;
  /** Accessible name for the navigation landmark. */
  label?: string;
  className?: string;
};

/**
 * Figma: `Sidebar` (53:3371) — "Main navigation container. Fixed position, full
 * viewport height. Width: 200px. Contains navigation items, footer, and
 * logomark. Wraps internal auto-layout group for vertical stacking."
 *
 * Height fills the parent rather than being pinned to the 900px of the Figma
 * frame, which is what "full viewport height" means in a real layout.
 */
export function Sidebar({
  sections,
  activeItemId,
  onSelect,
  footer,
  header,
  notification,
  label = 'Main',
  className,
}: SidebarProps) {
  /*
   * The travelling active fill — see the stylesheet for why it is one element
   * rather than a property on each row.
   *
   * Geometry is read from the row the markup already marks as current, so there
   * is no second source of truth about which row is selected and no prop to
   * thread down through SidebarItemGroup and SidebarItem.
   */
  const shellRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{
    x: number;
    y: number;
    w: number;
    h: number;
    radius: string;
  } | null>(null);

  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    const measure = () => {
      const row = shell.querySelector<HTMLElement>('[aria-current="page"]');
      setRect(
        row
          ? {
              x: row.offsetLeft,
              y: row.offsetTop,
              w: row.offsetWidth,
              h: row.offsetHeight,
              /*
               * The corner comes off the row for the same reason the other four
               * numbers do. It was restated as `radius/md` in the stylesheet and
               * silently went wrong when the 2026-08 update took rows down to
               * `radius/sm`: the block kept 8px corners on a 6px row. Reading it
               * means the next radius change cannot desynchronise them either.
               */
              radius: getComputedStyle(row).borderRadius,
            }
          : null,
      );
    };

    measure();

    /*
     * Rows move for reasons that are not a selection change: the webfont lands
     * and a section label reflows, the window narrows, a counter appears. The
     * observer keeps the block on its row through all of them.
     */
    const observer = new ResizeObserver(measure);
    observer.observe(shell);
    return () => observer.disconnect();
  }, [activeItemId, sections]);

  /*
   * Transitions are switched on one frame AFTER the block first has a position,
   * so it fades in where it belongs instead of sliding there from the top of the
   * nav. Dropping back to false when the selection is cleared means the next
   * selection fades in place too, rather than travelling from the last row.
   */
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    if (!rect) {
      setAnimated(false);
      return;
    }
    const frame = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(frame);
  }, [rect]);

  const indicatorClasses = [
    styles.indicator,
    rect ? styles.visible : null,
    animated ? styles.animated : null,
  ]
    .filter(Boolean)
    .join(' ');

  const indicatorStyle = {
    '--indicator-x': `${rect?.x ?? 0}px`,
    '--indicator-y': `${rect?.y ?? 0}px`,
    '--indicator-w': `${rect?.w ?? 0}px`,
    '--indicator-h': `${rect?.h ?? 0}px`,
    '--indicator-radius': rect?.radius,
  } as CSSProperties;

  return (
    <div className={className ? `${styles.sidebar} ${className}` : styles.sidebar}>
      <div className={styles.content}>
        <Stack direction="column" gap="12" fullWidth>
          <SidebarHeader
            actions={
              notification && (
                <NotificationButton
                  label={notification.label}
                  icon={<Icon name={notification.icon ?? 'inbox'} />}
                  unread={notification.unread}
                  unreadLabel={notification.unreadLabel}
                  onClick={notification.onClick}
                />
              )
            }
          >
            {header}
          </SidebarHeader>

          <div className={styles.navShell} ref={shellRef}>
            <span className={indicatorClasses} style={indicatorStyle} aria-hidden="true" />

            {/* Figma `Navigation` (48:2888) — `space/20` between groups since the
                2026-09 update, `space/8` inset either side. */}
            <Stack as="nav" direction="column" gap="20" paddingX="8" fullWidth aria-label={label}>
              {sections.map((section) => (
                <SidebarItemGroup key={section.id} label={section.label}>
                  {section.items.map((item) => (
                    <SidebarItem
                      key={item.id}
                      label={item.label}
                      href={item.href}
                      icon={item.icon && <Icon name={item.icon} />}
                      badge={
                        item.badge && (
                          <Badge type={item.badge.type} aria-label={item.badge.srLabel}>
                            {item.badge.label}
                          </Badge>
                        )
                      }
                      counter={
                        item.counter && (
                          <FilterChipCounter
                            count={item.counter.count}
                            label={item.counter.label}
                          />
                        )
                      }
                      state={item.id === activeItemId ? 'active' : undefined}
                      onClick={onSelect && (() => onSelect(item.id))}
                    />
                  ))}
                </SidebarItemGroup>
              ))}
            </Stack>
          </div>
        </Stack>

        {footer && (
          <SidebarFooter
            name={footer.name}
            avatarSrc={footer.avatarSrc}
            open={footer.open}
            onOpenChange={footer.onOpenChange}
            profile={footer.profile}
            credits={
              footer.credits && (
                <AICredits left={footer.credits.left} total={footer.credits.total} />
              )
            }
            menu={
              (footer.menu !== undefined || footer.onSignOut !== undefined) && (
                <>
                  {footer.menu?.map((item) => (
                    <SidebarItem
                      key={item.id}
                      label={item.label}
                      href={item.href}
                      icon={item.icon && <Icon name={item.icon} />}
                      onClick={item.onClick}
                    />
                  ))}
                  {/* Figma's fourth row, `logout` — see `SidebarFooterConfig.onSignOut`. */}
                  {footer.onSignOut && (
                    <SidebarItem
                      label={footer.signOutLabel ?? 'Sign out'}
                      icon={<Icon name="logout" />}
                      onClick={footer.onSignOut}
                    />
                  )}
                </>
              )
            }
          />
        )}
      </div>
    </div>
  );
}
